import { useTranslate } from '@tolgee/react';
import { MatchDetailItemFragment } from '@amf/shared/types/graphql';
import MatchEventItem from '@amf/shared/components/match/MatchEventItem';
import Pulser from '@amf/shared/components/utils/Pulser';
import { isSystemMatchEvent, isUsingSystemMatchEvents } from '@amf/shared/data/Match';
import { useMemo } from 'react';

interface MatchEventsSeparatorProps {
  title: string;
}

function MatchEventsSeparator({ title }: MatchEventsSeparatorProps) {
  return <div className='MatchEvents__separator'>{title}</div>;
}

interface MatchEventsTimelineProps {
  match: MatchDetailItemFragment;
  playerPrefix: string;
  offensePrefix?: string;
  homeTeamId: string;
  facr?: boolean;
}

function MatchEventsTimeline({
  match,
  playerPrefix,
  homeTeamId,
  offensePrefix,
  facr,
}: MatchEventsTimelineProps) {
  const { t } = useTranslate();
  const usingSystemMatchEvents = isUsingSystemMatchEvents(match);

  const allEvents = useMemo(
    () =>
      [...match.eventsStandard, ...match.eventsExtension, ...match.eventsPenalty].sort((a, b) => {
        if (a.minute > b.minute) return 1;
        if (a.minute < b.minute) return -1;
        return 0;
      }),
    [match],
  );

  if (usingSystemMatchEvents) {
    return (
      <>
        {allEvents.map(event =>
          isSystemMatchEvent(event) ? (
            <MatchEventsSeparator key={event.id} title={t(`${event.title}.title`)} />
          ) : (
            <MatchEventItem
              key={event.id}
              event={event}
              playerPrefix={playerPrefix}
              position={homeTeamId === event.teamId ? 'left' : 'right'}
              team={homeTeamId === event.teamId ? match.home.team : match.visiting.team}
              offensePrefix={offensePrefix}
              facr={facr}
            />
          ),
        )}
        {match.dateEnd === null && (
          <div className='MatchEvents__separator MatchEvents__separator--circle'>
            <Pulser />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {match.eventsStandard.map(event =>
        isSystemMatchEvent(event) ? null : (
          <MatchEventItem
            key={event.id}
            event={event}
            playerPrefix={playerPrefix}
            position={homeTeamId === event.teamId ? 'left' : 'right'}
            team={homeTeamId === event.teamId ? match.home.team : match.visiting.team}
            offensePrefix={offensePrefix}
            facr={facr}
          />
        ),
      )}
      {match.eventsExtension.length > 0 && (
        <>
          <MatchEventsSeparator title={t('match.event.list.end.regular')} />
          {match.eventsExtension.map(event =>
            isSystemMatchEvent(event) ? null : (
              <MatchEventItem
                key={event.id}
                event={event}
                playerPrefix={playerPrefix}
                position={homeTeamId === event.teamId ? 'left' : 'right'}
                team={homeTeamId === event.teamId ? match.home.team : match.visiting.team}
                offensePrefix={offensePrefix}
                facr={facr}
              />
            ),
          )}
        </>
      )}
      {match.eventsPenalty.length > 0 && (
        <>
          {match.eventsExtension.length === 0 ? (
            <MatchEventsSeparator title={t('match.event.list.end.regular')} />
          ) : (
            <MatchEventsSeparator title={t('match.event.list.end.extension')} />
          )}
          {match.eventsPenalty.map(event =>
            isSystemMatchEvent(event) ? null : (
              <MatchEventItem
                key={event.id}
                event={event}
                playerPrefix={playerPrefix}
                position={homeTeamId === event.teamId ? 'left' : 'right'}
                team={homeTeamId === event.teamId ? match.home.team : match.visiting.team}
                offensePrefix={offensePrefix}
                facr={facr}
              />
            ),
          )}
        </>
      )}
      {match.dateEnd === null ? (
        <div className='MatchEvents__separator MatchEvents__separator--circle'>
          <Pulser />
        </div>
      ) : (
        <MatchEventsSeparator title={t('match.event.list.end')} />
      )}
    </>
  );
}

export default MatchEventsTimeline;
